"use client";

import React, { useMemo, useState } from "react";

import { ServicesPricingSkillIcon } from "./ServicesPricingSkillIcon";
import {
  buildServicesPricingOfferingDisplays,
  buildServicesPricingSkillDisplays,
  sortServicesPricingOfferingsByPrice,
  type SkillInput,
} from "./profileServicesPricingDisplay";
import { ServicesPricingOfferingViewAllItem } from "./ServicesPricingOfferingViewAllItem";
import type { ServicesPricingOfferingViewAllItemLabels } from "./ServicesPricingOfferingViewAllItem";
import {
  ProfileServicesPricingSection,
  type ProfileServicesPricingCapabilityProjection,
  type ProfileServicesPricingSectionProps,
} from "./ProfileServicesPricingSection";

function skillNameFromProjection(
  skillKey: string,
  projection?: ProfileServicesPricingCapabilityProjection,
): string {
  const match = projection?.skills.find(
    (entry) => entry.publicSkillKey.endsWith(`:${skillKey}`) || entry.publicSkillKey.includes(skillKey),
  );
  return match?.name ?? skillKey.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export type ProfileServicesPricingViewAllLabels = Readonly<{
  closeLabel: string;
  offeringsViewAllTitle: string;
  offeringsViewAllDescription: (count: number) => string;
  offeringsViewAllItemLabels: ServicesPricingOfferingViewAllItemLabels;
  skillsViewAllTitle: string;
  skillsViewAllDescription: string;
}>;

export type ProfileServicesPricingPublicSectionProps = ProfileServicesPricingSectionProps & Readonly<{
  viewAllLabels: ProfileServicesPricingViewAllLabels;
}>;

function ProfileServicesPricingViewAllModal({
  children,
  componentInstance,
  description,
  isOpen,
  onClose,
  title,
  closeLabel,
}: Readonly<{
  children: React.ReactNode;
  componentInstance: string;
  description?: string;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  closeLabel: string;
}>) {
  if (!isOpen) return null;

  return (
    <div
      className="profile-services-pricing-view-all-modal"
      data-component-instance={componentInstance}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${componentInstance}-title`}
    >
      <button
        type="button"
        className="profile-services-pricing-view-all-modal__backdrop"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div className="profile-services-pricing-view-all-modal__panel">
        <div className="profile-services-pricing-view-all-modal__header">
          <div>
            <h2 id={`${componentInstance}-title`}>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button type="button" onClick={onClose}>{closeLabel}</button>
        </div>
        <div className="profile-services-pricing-view-all-modal__body">{children}</div>
      </div>
    </div>
  );
}

export function ProfileServicesPricingPublicSection({
  capabilityProjection,
  capabilityRecords,
  componentInstance = "profileServicesPricingPublicSection",
  labels,
  serviceRecords,
  viewAllLabels,
  ...sectionProps
}: ProfileServicesPricingPublicSectionProps) {
  const [offeringsViewAllOpen, setOfferingsViewAllOpen] = useState(false);
  const [skillsViewAllOpen, setSkillsViewAllOpen] = useState(false);

  const viewAllOfferings = useMemo(
    () => sortServicesPricingOfferingsByPrice(buildServicesPricingOfferingDisplays(serviceRecords, {
      fromLabel: labels.offeringsFromLabel,
      quoteRequiredLabel: labels.offeringsQuoteRequiredLabel,
    })),
    [labels.offeringsFromLabel, labels.offeringsQuoteRequiredLabel, serviceRecords],
  );

  const viewAllSkills = useMemo(() => {
    const skillInputs: SkillInput[] = capabilityRecords.skills.map((skill) => ({
      id: skill.id,
      skillKey: skill.skillKey,
      skillLevel: skill.skillLevel,
      validationStatus: skill.validationStatus,
      confirmationCount: skill.confirmationCount,
      iconKey: skill.iconKey,
      featuredValidatorInitials: skill.featuredValidatorInitials,
    }));
    return buildServicesPricingSkillDisplays(skillInputs).map((entry) => {
      const source = capabilityRecords.skills.find((skill) => skill.id === entry.id);
      return source
        ? { ...entry, name: skillNameFromProjection(source.skillKey, capabilityProjection) }
        : entry;
    });
  }, [capabilityProjection, capabilityRecords.skills]);

  return (
    <>
      <ProfileServicesPricingSection
        {...sectionProps}
        capabilityProjection={capabilityProjection}
        capabilityRecords={capabilityRecords}
        componentInstance={componentInstance}
        labels={labels}
        onViewAllOfferings={() => setOfferingsViewAllOpen(true)}
        onViewAllSkills={() => setSkillsViewAllOpen(true)}
        serviceRecords={serviceRecords}
      />

      <ProfileServicesPricingViewAllModal
        closeLabel={viewAllLabels.closeLabel}
        componentInstance={`${componentInstance}OfferingsViewAllModal`}
        description={viewAllLabels.offeringsViewAllDescription(viewAllOfferings.length)}
        isOpen={offeringsViewAllOpen}
        onClose={() => setOfferingsViewAllOpen(false)}
        title={viewAllLabels.offeringsViewAllTitle}
      >
        <div className="profile-services-pricing-view-all-list">
          {viewAllOfferings.map((offering) => (
            <ServicesPricingOfferingViewAllItem
              key={offering.id}
              labels={viewAllLabels.offeringsViewAllItemLabels}
              offering={offering}
            />
          ))}
        </div>
      </ProfileServicesPricingViewAllModal>

      <ProfileServicesPricingViewAllModal
        closeLabel={viewAllLabels.closeLabel}
        componentInstance={`${componentInstance}SkillsViewAllModal`}
        description={viewAllLabels.skillsViewAllDescription}
        isOpen={skillsViewAllOpen}
        onClose={() => setSkillsViewAllOpen(false)}
        title={viewAllLabels.skillsViewAllTitle}
      >
        <div className="profile-services-pricing-skills">
          {viewAllSkills.map((skill) => (
            <article key={skill.id} className="profile-services-pricing-skill" data-skill-tone={skill.visualTone}>
              <div className="profile-services-pricing-skill__header">
                <ServicesPricingSkillIcon
                  className="profile-services-pricing-skill__icon"
                  iconKey={skill.iconKey}
                />
                <div className="profile-services-pricing-skill__title-row">
                  <strong>{skill.name}</strong>
                  {skill.validatorInitials.length > 0 ? (
                    <span className="profile-services-pricing-skill__validators">
                      {skill.validatorInitials.map((initials) => (
                        <span key={`${skill.id}-${initials}`}>{initials}</span>
                      ))}
                    </span>
                  ) : null}
                </div>
                <span className="profile-services-pricing-skill__badge">
                  {labels.skillsValidationCount(skill.validationCount)}
                </span>
              </div>
              <div className="profile-services-pricing-skill__progress" aria-hidden="true">
                <span style={{ width: `${skill.validationPercent}%` }} />
              </div>
            </article>
          ))}
        </div>
      </ProfileServicesPricingViewAllModal>
    </>
  );
}
